import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowDown, ArrowLeft } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";

const HERO_IMG = "/brand/vincen-star.jpg";
const HERO_IMG_LIGHT = "/brand/vincen-star-light.png";
const AUTO_IMG =
  "https://images.pexels.com/photos/18516675/pexels-photo-18516675.jpeg?auto=compress&cs=tinysrgb&w=1600";
const CINEMA_IMG =
  "https://images.pexels.com/photos/2873486/pexels-photo-2873486.jpeg?auto=compress&cs=tinysrgb&w=1200";
const WILDEN_IMG =
  "https://images.pexels.com/photos/27270589/pexels-photo-27270589/free-photo-of-a-lone-wolf-in-the-woods-at-night.jpeg?auto=compress&cs=tinysrgb&w=1200";
const OTTER_IMG = "/brand/pebble-otter.jpg";

const chapters = [
  {
    num: "I",
    title: "The Origin",
    text: "Vincen. From the Latin vincere \u2014 to conquer. A maison born where victory is not celebrated, but worn.",
  },
  {
    num: "II",
    title: "The Fusion",
    text: "A football crest meets a racing marque. A club anthem meets a cult series. Two legacies, cut into a single garment.",
  },
  {
    num: "III",
    title: "The Craft",
    text: "Match-day jerseys to heavyweight hoodies and zippers — technical knits, fleece and satin, finished with embroidered gold thread. Every fabric is chosen for the piece, never the other way round.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const [featured, setFeatured] = useState([]);
  const [colSlide, setColSlide] = useState(0);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
          <img src={HERO_IMG} alt="Vincen star emblem" className="w-full h-full object-contain bg-black lux-hero-dark" />
          <div className="absolute inset-0 bg-black/30 lux-hero-veil" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/40 lux-hero-grad" />
          <div className="lux-hero-light absolute inset-0 hidden flex-col items-center justify-end pb-[6vh]">
            <img src={HERO_IMG_LIGHT} alt="Vincen star emblem" className="lux-hero-light-star h-[82%] w-auto object-contain" />
            <span className="lux-vincen-black font-serif font-light leading-none tracking-[0.42em] text-6xl sm:text-8xl lg:text-[7rem] -mt-[4vh] pl-[0.42em]">
              VINCEN
            </span>
          </div>
        </motion.div>

        <motion.div
          style={{ opacity: contentOpacity }}
          className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32"
        >
          <MaskedLine delay={0.3}>
            <Overline>Dress to Conquer</Overline>
          </MaskedLine>
          <h1 className="mt-6 font-serif font-light leading-[0.95] text-[#F5F5F5]">
            <MaskedLine delay={0.5} className="text-5xl sm:text-7xl lg:text-8xl tracking-tight">
              THE ONE
            </MaskedLine>
            <MaskedLine delay={0.65} className="text-5xl sm:text-7xl lg:text-8xl tracking-tight">
              WHO <span className="italic text-gold">CONQUERS</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.9}>
            <p className="mt-8 max-w-md text-sm md:text-base text-zinc-400 leading-relaxed">
              Vincen is a luxury house built on old-money codes &mdash; quiet, deliberate,
              unmistakable. Nothing here is merely worn; each piece is a collectable,
              acquired by those who conquer without ever asking.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-explore-button"
              className="group px-10 py-4 bg-gold text-black text-xs tracking-[0.35em] uppercase font-semibold hover:bg-gold-hover transition-colors duration-500 flex items-center gap-3"
            >
              Explore Collections
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <a
              href="#manifesto"
              data-testid="hero-manifesto-button"
              className="px-10 py-4 border border-white/20 text-zinc-300 text-xs tracking-[0.35em] uppercase hover:border-maroon-light hover:bg-maroon/40 hover:text-[#F5F5F5] transition-colors duration-500"
            >
              The Manifesto
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-8 right-8 md:right-12 z-10 text-zinc-500"
        >
          <ArrowDown size={18} className="animate-bounce" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 border-y border-white/5 bg-[#0A0A0A]">
        <Marquee speed={25} gradient={false} pauseOnHover>
          {[
            { t: "Football \u00D7 Automobile", c: "automobile" },
            { t: "Football \u00D7 Cinema", c: "cinema" },
            { t: "Football \u00D7 Wilden", c: "wilden" },
            { t: "Pebble The Otter", c: "otter" },
            { t: "Vincen Originals", c: "originals" },
            { t: "Vincen Plus", c: "plus" },
          ].map(({ t, c }, i) => (
            <Link
              key={i}
              to={`/shop?collab=${c}`}
              data-testid={`marquee-link-${c}`}
              className="flex items-center mx-6 group"
            >
              <span className="font-serif text-2xl md:text-3xl font-light italic text-zinc-600 whitespace-nowrap group-hover:text-gold transition-colors duration-300">
                {t}
              </span>
              <span className={`w-1.5 h-1.5 rotate-45 ml-12 ${i % 2 ? "bg-maroon-light/70" : "bg-gold/50"}`} />
            </Link>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-40">
        <FadeUp>
          <Overline>The Manifesto</Overline>
        </FadeUp>
        <div className="mt-16 flex flex-col gap-24 md:gap-32">
          {chapters.map((c, i) => (
            <FadeUp key={c.num} delay={0.1}>
              <div
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-start ${
                  i % 2 === 1 ? "md:text-right" : ""
                }`}
              >
                <div className={`md:col-span-4 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <span className={`font-serif text-8xl md:text-[11rem] font-light leading-none tracking-tight ${i % 2 === 1 ? "lux-numeral-maroon" : "lux-numeral"}`}>
                    {c.num}
                  </span>
                </div>
                <div className={`md:col-span-8 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <h3 className="font-serif text-3xl md:text-5xl font-light text-[#F5F5F5]">{c.title}</h3>
                  <p className={`mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl ${i % 2 === 1 ? "md:ml-auto" : ""}`}>
                    {c.text}
                  </p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* COLLECTIONS SLIDER */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-28 md:pb-40">
        <FadeUp>
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <Overline>The Collections</Overline>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5]">
                Two Worlds, <span className="italic text-gold">One Cloth</span>
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <button
                data-testid="collections-prev"
                onClick={() => setColSlide(0)}
                disabled={colSlide === 0}
                aria-label="Previous collections"
                className="w-12 h-12 border border-white/15 flex items-center justify-center text-zinc-400 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ArrowLeft size={16} />
              </button>
              <span data-testid="collections-indicator" className="text-xs tracking-[0.3em] text-zinc-500">
                0{colSlide + 1} / 02
              </span>
              <button
                data-testid="collections-next"
                onClick={() => setColSlide(1)}
                disabled={colSlide === 1}
                aria-label="Next collections"
                className="w-12 h-12 border border-white/15 flex items-center justify-center text-zinc-400 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </FadeUp>

        <div className="overflow-hidden" data-testid="collections-slider">
          <motion.div
            className="flex"
            animate={{ x: colSlide === 0 ? "0%" : "-100%" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  to: "/shop?collab=automobile",
                  img: AUTO_IMG,
                  label: "Collection I",
                  title: "Football \u00D7 Automobile",
                  text: "Racing-bred silhouettes. Club crests stitched beside marque insignia.",
                  testid: "collection-automobile-card",
                },
                {
                  to: "/shop?collab=cinema",
                  img: CINEMA_IMG,
                  label: "Collection II",
                  title: "Football \u00D7 Cinema",
                  text: "Cult series and film iconography, reimagined on match-day silhouettes.",
                  testid: "collection-cinema-card",
                  accent: true,
                },
                {
                  to: "/shop?collab=wilden",
                  img: WILDEN_IMG,
                  label: "Collection III",
                  title: "Football \u00D7 Wilden",
                  text: "Untamed by nature. Wilderness-grade textures meet the beautiful game.",
                  testid: "collection-wilden-card",
                },
              ].map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={c.testid}
                  className={`group relative block h-[420px] md:h-[520px] overflow-hidden border ${
                    c.accent ? "border-maroon/60 hover:border-maroon-light" : "border-white/10 hover:border-gold/40"
                  } transition-colors duration-500`}
                >
                  <img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  {c.accent && <div className="absolute inset-0 bg-gradient-to-t from-maroon-deep/50 to-transparent" />}
                  <div className="absolute bottom-0 left-0 p-8 lux-overcard">
                    <p className={`text-[10px] tracking-[0.35em] uppercase ${c.accent ? "text-maroon-light" : "text-gold"}`}>{c.label}</p>
                    <h3 className="mt-3 font-serif text-2xl md:text-4xl font-light text-[#F5F5F5]">{c.title}</h3>
                    <p className="mt-3 text-sm text-zinc-400 max-w-xs">{c.text}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-gold group-hover:gap-4 transition-all duration-500">
                      Enter <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="w-full flex-shrink-0">
              <Link
                to="/shop?collab=otter"
                data-testid="collection-otter-card"
                className="group relative block h-[480px] md:h-[560px] overflow-hidden border border-gold/30"
              >
                <img
                  src={OTTER_IMG}
                  alt="Pebble The Otter collection"
                  className="w-full h-full object-cover object-center transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-maroon-deep/40 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8 md:p-12 lux-overcard">
                  <p className="text-[10px] tracking-[0.35em] uppercase text-gold">The Mascot Series</p>
                  <h3 className="mt-3 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5]">
                    Pebble <span className="italic text-gold">The Otter</span>
                  </h3>
                  <p className="mt-4 text-sm text-zinc-400 max-w-md">
                    Our little heart of the house. Pebble is the spirit of Vincen &mdash;
                    warmth, gentleness, and the quiet care that keeps everyone close.
                    A softer world, made to be loved.
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-gold group-hover:gap-4 transition-all duration-500">
                    Enter <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-28 md:pb-40" data-testid="featured-section">
          <FadeUp>
            <div className="flex items-end justify-between mb-12">
              <div>
                <Overline>Featured Pieces</Overline>
                <h2 className="mt-4 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5]">
                  The <span className="italic text-gold">Conquest</span> Edit
                </h2>
              </div>
              <Link
                to="/shop"
                data-testid="featured-view-all-link"
                className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-zinc-400 hover:text-gold transition-colors duration-300"
              >
                View All <ArrowRight size={13} />
              </Link>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="relative border-t border-maroon/40 py-28 md:py-36 text-center px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-maroon-deep/30 via-transparent to-transparent pointer-events-none" />
        <FadeUp>
          <p className="font-serif italic text-2xl md:text-4xl text-zinc-500">Veni. Vidi.</p>
          <h2 className="mt-4 font-serif text-5xl md:text-7xl font-light text-[#F5F5F5]">
            <span className="text-gold">Vincen.</span>
          </h2>
          <Link
            to="/shop"
            data-testid="closing-cta-button"
            className="mt-10 inline-block px-12 py-4 bg-maroon border border-maroon text-[#F5F5F5] text-xs tracking-[0.35em] uppercase hover:bg-gold hover:border-gold hover:text-black transition-colors duration-500"
          >
            Claim Your Piece
          </Link>
        </FadeUp>
      </section>
    </div>
  );
};

export default Home;
